"use client";
import {
  InputSearchBar,
  InputSearchBarElement,
  GlossaryLetters,
  GlossaryLetter,
} from "@ama-pt/agora-design-system";
import { ChangeEvent, MouseEvent, useEffect, useRef, useState } from "react";
import { useRouter, usePathname, useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";
import { GlossaryItem } from "@/services/glossary/types";
import { getGlossaries } from "@/services/glossary/actions";
import { letters } from "./letters";
import { RichText } from "../RichText/RichText";
export interface GlossaryProps {
  searchTitle: string;
  searchPlaceholder: string;
  initialItems?: GlossaryItem[];
}

export const Glossary = ({
  searchPlaceholder = "",
  searchTitle = "",
}: GlossaryProps) => {
  const searchParams = useSearchParams();
  const router = useRouter();
  const translations = useTranslations();

  const [isListening, setIsListening] = useState(false);
  const [items, setItems] = useState<GlossaryItem[] | null>(null);

  const [glossaryLetters, setGlossaryLetters] = useState([
    ...letters,
  ] as GlossaryLetter[]);

  const pathname = usePathname();

  const searchTerm = searchParams?.get("search") || "";
  const searchRef = useRef<InputSearchBarElement>(null);

  const [searchValue, setSearchValue] = useState(searchTerm);
  const onSearchChangeHandler = (evt: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(evt.target.value);
  };

  const onSearchActivateHandler = (term: string) => {
    const params = new URLSearchParams(searchParams?.toString());

    if (term.trim()) {
      params.set("search", term.trim());
    } else {
      params.delete("search");
    }

    params.set("page", "1");
    router.push(`${pathname}?${params.toString()}`);
  };

  const onVoiceActivateHandler = () => {
    if (
      !("webkitSpeechRecognition" in window) &&
      !("SpeechRecognition" in window)
    ) {
      alert(
        "Seu navegador não suporta reconhecimento de voz. Tente usar Chrome, Edge ou Safari."
      );
      return;
    }

    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();

    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = "pt-PT";
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      setIsListening(true);
    };

    recognition.onresult = (event: any) => {
      const transcript = event.results[0][0].transcript;

      setSearchValue(transcript);

      setTimeout(() => {
        onSearchActivateHandler(transcript);
      }, 100);
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognition.onerror = (event: any) => {
      console.error("Erro no reconhecimento de voz:", event.error);
      setIsListening(false);

      switch (event.error) {
        case "network":
          alert("Erro de rede. Verifique sua conexão com a internet.");
          break;
        case "not-allowed":
          alert(
            "Permissão de microfone negada. Ative o microfone nas configurações do navegador."
          );
          break;
        case "no-speech":
          alert("Nenhuma fala detectada. Tente novamente.");
          break;
        case "audio-capture":
          alert("Microfone não encontrado. Verifique se está conectado.");
          break;
        default:
          alert(`Erro no reconhecimento de voz: ${event.error}`);
      }
    };

    try {
      recognition.start();
    } catch (error) {
      console.error("Erro ao iniciar reconhecimento:", error);
      setIsListening(false);
      alert("Erro ao iniciar reconhecimento de voz. Tente novamente.");
    }
  };

  const fetchItems = async (query: string, hash?: string) => {
    const response = await getGlossaries(query);
    const items = response?.glossariesData?.items ?? [];

    setItems(items);

    const initialsSet = new Set(
      items.map((item) => item.title?.[0]?.toUpperCase()).filter(Boolean)
    );

    setGlossaryLetters((prevLetters) =>
      prevLetters.map((letter) => {
        const upper = letter.letter.toUpperCase();
        return {
          ...letter,
          status: initialsSet.has(upper)
            ? hash === upper
              ? "selected"
              : "default"
            : "disabled",
        };
      })
    );
  };
  useEffect(() => {
    const term = searchParams?.get("search") || "";
/*     const hash =
      typeof window !== "undefined"
        ? window.location.hash.replace("#", "").toUpperCase()
        : ""; */

    fetchItems(term);
    setSearchValue(term);
  }, [searchParams]);

  const handleClick = (evt: MouseEvent<HTMLDivElement>) => {
    const glossaryLetter = (evt.target as HTMLElement).getAttribute(
      "data-glossary-letter"
    );

    if (glossaryLetter) {
      setGlossaryLetters((lastLetters) => {
        const isCurrentlySelected = lastLetters.find(
          (l) => l.letter === glossaryLetter && l.status === "selected"
        );

        const newLetters = lastLetters.map((l) => {
          const lastRawStatus =
            l.status === "disabled"
              ? ("disabled" as GlossaryLetter["status"])
              : ("default" as GlossaryLetter["status"]);

          if (l.letter !== glossaryLetter) {
            return { ...l, status: lastRawStatus };
          }

          return {
            ...l,
            status: isCurrentlySelected
              ? lastRawStatus
              : ("selected" as GlossaryLetter["status"]),
          };
        });

        return newLetters;
      });
    }
  };

  return (
    <div className="flex w-full overflow-hidden">
      <div className="container mx-auto">
        <div className="grid items-start w-full grid-cols-4 md:grid-cols-8 xl:grid-cols-12 gap-[24px]">
          <div className="col-span-4 md:col-span-2 xl:col-span-3 custom-glossary overflow-auto">
              <GlossaryLetters
                onClick={handleClick}
                letters={glossaryLetters}
              />
          </div>

          <div className="col-span-4  md:col-span-6 xl:col-span-8 xl:col-start-5">
            <InputSearchBar
              ref={searchRef}
              id="glossary-search-input"
              className=" text-primary-500"
              label={searchTitle}
              placeholder={searchPlaceholder}
              voiceActionAltText={
                isListening
                  ? "Escutando..."
                  : translations("SearchPanel.searchWithVoice")
              }
              searchActionAltText={translations("SearchPanel.search")}
              value={searchValue}
              onChange={onSearchChangeHandler}
              onSearchActivate={onSearchActivateHandler}
              onVoiceActivate={onVoiceActivateHandler}
              onKeyDown={(evt) => {
                if (evt.key === "Enter") {
                  onSearchActivateHandler(searchValue);
                }
              }}
            />

            <div className="flex sticky flex-col gap-[32px] mt-[44px] md:mt-[64px]">
              {Array.isArray(items) &&
                [
                  ...items
                    .map((e, i) => ({
                      title: e.title.substring(0, 1),
                      i: "x" + i,
                      type: "letter",
                      description: "",
                    }))
                    .filter((item) => {
                      const activeLetters = glossaryLetters
                        .filter((btn) => btn.status === "selected")
                        .map((btn) => btn.letter.toLowerCase());

                      if (activeLetters.length === 0) return true;

                      return activeLetters.some((letter) =>
                        item.title?.toLowerCase().startsWith(letter)
                      );
                    })
                    .filter(
                      (item, index, self) =>
                        self.findIndex(
                          (i) =>
                            i.title?.toLowerCase() ===
                            item.title?.toLowerCase()
                        ) === index
                    ),
                  ...items
                    .map((e, i) => ({ ...e, i, type: "item" }))
                    .filter((item) => {
                      const activeLetters = glossaryLetters
                        .filter((btn) => btn.status === "selected")
                        .map((btn) => btn.letter.toLowerCase());

                      if (activeLetters.length === 0) return true;

                      return activeLetters.some((letter) =>
                        item.title?.toLowerCase().startsWith(letter)
                      );
                    }),
                ]
                  .sort((a, b) => {
                    const charA = a.title?.[0]?.toLowerCase() || "";
                    const charB = b.title?.[0]?.toLowerCase() || "";
                    return charA.localeCompare(charB);
                  })
                  .map((e) => {
                    if (e.type === "letter")
                      return (
                        <div
                          key={e.i}
                          className="w-[64px] st flex items-center justify-center rounded-full bg-primary-100 h-[64px]"
                        >
                          <p className="text-l-bold text-primary-900  p-0 m-0">
                            {e.title}
                          </p>
                        </div>
                      );
                    if (e.type === "item")
                      return (
                        <div key={e.i} className="flex flex-col gap-[4px]">
                          <p className="text-l-bold text- p-0 m-0">
                            {e.title}
                          </p>
                          <RichText content={e.description} 
                            className="text-m-regular text-neutral-900"
                          />
                        </div>
                      );
                  })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
